import { useRef, useCallback, useEffect, useState } from 'react';
import { Map } from 'maplibre-gl';
import { MapContextProvider, useMap } from '@/hooks/useMap';
import { cn } from '@/utils/styles';
import { addLayer as addCoverageLayer } from '@/components/ui/map/layers/Coverage';
import * as CoverageData from '@/components/ui/map/sources/NewCoverageData';
import { IMapType, IMapOptions } from '@/types/map';

interface CoverageMapProps {
  children?: React.ReactNode;
  className?: string;
  options?: Partial<IMapOptions>;
  includeContext?: boolean;
  clickToInteract?: boolean;
}

const MAX_ZOOM_WITH_COVERAGE = 15.99;

interface MapContainerProps extends CoverageMapProps {
  onClick?: () => void;
  interactionEnabled?: boolean;
}

const MapContainer = ({
  className,
  children,
  onClick,
  clickToInteract,
}: MapContainerProps) => {
  const mapMounted = useRef(false);
  const [interactionEnabled, setInteractionEnabled] =
    useState(!clickToInteract);
  const { map, setAreLayersReady, mapContainerRef, currentCoords } = useMap();

  const initializeData = useCallback(
    async (
      map: Map,
      setAreLayersReady: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      setAreLayersReady(false);

      map.setMaxZoom(MAX_ZOOM_WITH_COVERAGE);
      await CoverageData.addSource(map);
      addCoverageLayer(map, {
        type: 'coverage',
      });

      setAreLayersReady(true);
    },
    [],
  );

  useEffect(() => {
    if (map && !mapMounted.current) {
      initializeData(map, setAreLayersReady);
      mapMounted.current = true;
    }
  }, [map, initializeData, setAreLayersReady]);

  return (
    <div
      ref={mapContainerRef}
      onClick={onClick}
      className={cn('relative flex size-full', className)}>
      <div
        onClick={() => setInteractionEnabled(true)}
        className={cn(
          'absolute inset-0 z-10 cursor-pointer',
          interactionEnabled ? 'hidden' : 'block',
        )}
      />
      {children}
    </div>
  );
};

const CoverageMap = ({
  className,
  options,
  children,
  clickToInteract = false,
  includeContext = true,
}: CoverageMapProps) => {
  const [screenWidth, setScreenWidth] = useState<number | undefined>();

  const mapType: IMapType = 'coverage';
  const defaultOptions: IMapOptions = {
    enableStyles: false,
    enableLocation: true,
    enableSearch: false,
    showMapboxInfo: false,
    enableSearchMarker: true,
  };
  const mapContextProps = {
    country: 'US',
    currentRegionName: '',
    screenWidth: screenWidth,
    mapType,
    options: {
      ...defaultOptions,
      ...options,
    },
  };

  useEffect(() => {
    if (window) {
      setScreenWidth(window.innerWidth);
    }
  }, []);

  return includeContext ? (
    <MapContextProvider {...mapContextProps}>
      <MapContainer
        clickToInteract={clickToInteract}
        className={cn('h-full w-full', className)}>
        {children}
      </MapContainer>
    </MapContextProvider>
  ) : (
    <MapContainer
      clickToInteract={clickToInteract}
      className={cn('h-full w-full', className)}>
      {children}
    </MapContainer>
  );
};

export default CoverageMap;
